import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { referralSchema, REFERRAL_LIMITS } from '../../../shared/schemas/referral';
import { HONEYPOT_FIELD } from '../../../shared/schemas/fields';
import { referralContent } from '../../content/referral';
import { postReferral } from '../../lib/api';
import { track } from '../../lib/analytics';
import { readSignupRef, type SignupAudience } from '../../lib/signupSession';
import { TextField } from '../forms/TextField';
import { TextAreaField } from '../forms/TextAreaField';
import { HoneypotField } from '../forms/HoneypotField';
import { FormErrorSummary, type SummaryFieldError } from '../forms/FormErrorSummary';
import { SubmitButton } from '../forms/SubmitButton';
import { fromServerFields, fromZodError, type FieldErrors } from '../forms/formErrors';
import { SharePrompt } from './SharePrompt';

/**
 * The referral ask on both thank-you pages (spec sections 10, 14, 16, 17).
 *
 * **Nothing here contacts the person named.** The submission writes one row in
 * an outreach queue that a human works by hand, and the only outbound message
 * in this flow is the one the referrer copies and sends themselves
 * (`SharePrompt`). Spec section 17 is the reasoning and it is not up for
 * revision.
 *
 * **This step is optional and it cannot damage the page it sits on.** The
 * confirmation above it is a success state that has already been earned. So
 * every failure path — validation, network, rate limit, a 500 — stays inside
 * this section: a message in the summary, focus moved to it, the button usable
 * again, and a line saying the early access spot is unaffected. The section
 * never navigates, never unmounts the confirmation, and never blocks it.
 *
 * On success the form is replaced in place by a thanks message and the share
 * prompt. Focus moves to the thanks so a screen reader hears the outcome rather
 * than being dropped into a suddenly different subtree.
 *
 * The signup token goes out in the request body, read from `sessionStorage` at
 * submit time. It never appears in a URL and the browser is never told what the
 * server made of it (spec section 19).
 */

/** Render order. Drives the order of entries in the error summary. */
const FIELD_ORDER = [
  'recommendedName',
  'categoryText',
  'locationText',
  'websiteOrContact',
  'note',
] as const;

/** Focusing one of these is someone starting to fill the form in. */
const CONTROL_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

function transportMessage(kind: 'network' | 'server' | 'rate_limited'): string {
  const copy = referralContent.common;
  if (kind === 'network') return copy.networkError;
  if (kind === 'rate_limited') return copy.rateLimitedError;
  return copy.serverError;
}

export interface ReferralFormProps {
  audience: SignupAudience;
  /** Id the surrounding section is labelled by. Owned by this component. */
  headingId: string;
}

export function ReferralForm({ audience, headingId }: ReferralFormProps) {
  const uid = useId();
  const fieldId = (name: string) => `${uid}-${name}`;
  const copy = referralContent[audience];
  const shared = referralContent.common;

  const [recommendedName, setRecommendedName] = useState('');
  const [categoryText, setCategoryText] = useState('');
  const [locationText, setLocationText] = useState('');
  const [websiteOrContact, setWebsiteOrContact] = useState('');
  const [note, setNote] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const summaryRef = useRef<HTMLDivElement | null>(null);
  const successRef = useRef<HTMLHeadingElement | null>(null);
  const startedRef = useRef(false);
  const focusSummaryRef = useRef(false);
  const focusSuccessRef = useRef(false);
  /** Set on unmount so a late response cannot call setState on a dead form. */
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Focus has to happen after the target has rendered, so it is requested by
  // the submit handler and performed here on the next commit.
  useEffect(() => {
    if (focusSummaryRef.current) {
      focusSummaryRef.current = false;
      summaryRef.current?.focus();
    }
    if (focusSuccessRef.current) {
      focusSuccessRef.current = false;
      successRef.current?.focus();
    }
  });

  const noteStarted = useCallback((event: { target: EventTarget | null }) => {
    if (startedRef.current) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!CONTROL_TAGS.has(target.tagName)) return;
    startedRef.current = true;
    track('referral_started');
  }, []);

  const showErrors = (next: FieldErrors, form: readonly string[]) => {
    setFieldErrors(next);
    setFormErrors([...form]);
    focusSummaryRef.current = true;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;

    const parsed = referralSchema.safeParse({
      recommendedName,
      categoryText,
      locationText,
      websiteOrContact,
      note,
      referrerType: audience,
      [HONEYPOT_FIELD]: honeypot,
      // Read at submit time, not at mount: the receipt is what attaches this
      // recommendation to the lead, and the server is what verifies it.
      signupRef: readSignupRef(audience) ?? undefined,
    });

    if (!parsed.success) {
      const { fieldErrors: next, formErrors: form } = fromZodError(parsed.error, FIELD_ORDER);
      showErrors(next, form);
      return;
    }

    setPending(true);
    setFieldErrors({});
    setFormErrors([]);

    void (async () => {
      const result = await postReferral(parsed.data);
      if (!mountedRef.current) return;

      if (result.ok) {
        // A honeypot submission gets this same response by design: the bot is
        // shown an ordinary success and nothing is written (routes/referrals.ts).
        track('referral_completed', { referrer: audience });
        focusSuccessRef.current = true;
        setSubmitted(true);
        return;
      }

      setPending(false);

      if (result.kind === 'validation') {
        const { fieldErrors: next, formErrors: form } = fromServerFields(
          result.fields,
          FIELD_ORDER,
        );
        const hasSomethingToShow = Object.keys(next).length > 0 || form.length > 0;
        showErrors(next, hasSomethingToShow ? form : [shared.unmappedFieldError]);
        return;
      }

      showErrors({}, [transportMessage(result.kind)]);
    })();
  };

  if (submitted) {
    return (
      <div className="rounded-lg border-2 border-ink/15 bg-field p-6 text-ink shadow-sm sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-signal">
          {copy.eyebrow}
        </p>
        <h2
          id={headingId}
          ref={successRef}
          tabIndex={-1}
          className="mt-3 max-w-2xl text-xl font-semibold text-ink focus:outline-2 focus:outline-offset-4 focus:outline-ink sm:text-2xl"
        >
          {copy.success.headline}
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/85">{copy.success.body}</p>
        <SharePrompt audience={audience} idPrefix={uid} />
      </div>
    );
  }

  const summaryEntries: SummaryFieldError[] = FIELD_ORDER.filter(
    (key) => fieldErrors[key] !== undefined,
  ).map((key) => ({
    targetId: fieldId(key),
    label: shared.fields[key].label,
    message: fieldErrors[key] ?? '',
  }));

  return (
    <div className="rounded-lg border-2 border-ink/15 bg-field p-6 text-ink shadow-sm sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-signal">
        {copy.eyebrow}
      </p>
      <h2 id={headingId} className="mt-3 max-w-2xl text-xl font-semibold text-ink sm:text-2xl">
        {copy.headline}
      </h2>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/85">{copy.body}</p>
      {/* Answered before it is asked: the site contacts nobody (spec §17). */}
      <p className="mt-3 max-w-2xl text-sm font-semibold leading-relaxed text-ink">
        {shared.privacyNote}
      </p>

      <form
        noValidate
        onSubmit={handleSubmit}
        onFocusCapture={noteStarted}
        aria-labelledby={headingId}
        aria-describedby={`${uid}-description`}
        className="mt-6 max-w-xl"
      >
        <FormErrorSummary
          id={`${uid}-errors`}
          ref={summaryRef}
          title={shared.errorSummaryTitle}
          fieldErrors={summaryEntries}
          formErrors={formErrors}
        />

        {formErrors.length > 0 ? (
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-ink/85">
            {shared.failureReassurance}
          </p>
        ) : null}

        <p id={`${uid}-description`} className="text-sm leading-relaxed text-ink/85">
          {shared.requiredNote}
        </p>

        <div className="mt-6 space-y-6">
          <TextField
            id={fieldId('recommendedName')}
            name="recommendedName"
            required
            maxLength={REFERRAL_LIMITS.recommendedName.max}
            label={shared.fields.recommendedName.label}
            hint={shared.fields.recommendedName.hint}
            value={recommendedName}
            onChange={setRecommendedName}
            error={fieldErrors.recommendedName}
          />

          <TextField
            id={fieldId('categoryText')}
            name="categoryText"
            maxLength={REFERRAL_LIMITS.categoryText}
            label={shared.fields.categoryText.label}
            hint={shared.fields.categoryText.hint}
            value={categoryText}
            onChange={setCategoryText}
            error={fieldErrors.categoryText}
          />

          <TextField
            id={fieldId('locationText')}
            name="locationText"
            maxLength={REFERRAL_LIMITS.locationText}
            label={shared.fields.locationText.label}
            hint={shared.fields.locationText.hint}
            value={locationText}
            onChange={setLocationText}
            error={fieldErrors.locationText}
          />

          <TextField
            id={fieldId('websiteOrContact')}
            name="websiteOrContact"
            maxLength={REFERRAL_LIMITS.websiteOrContact}
            label={shared.fields.websiteOrContact.label}
            hint={shared.fields.websiteOrContact.hint}
            value={websiteOrContact}
            onChange={setWebsiteOrContact}
            error={fieldErrors.websiteOrContact}
          />

          <TextAreaField
            id={fieldId('note')}
            name="note"
            rows={3}
            maxLength={REFERRAL_LIMITS.note}
            label={shared.fields.note.label}
            hint={shared.fields.note.hint}
            value={note}
            onChange={setNote}
            error={fieldErrors.note}
          />
        </div>

        <HoneypotField id={fieldId(HONEYPOT_FIELD)} value={honeypot} onChange={setHoneypot} />

        <div className="mt-8">
          <SubmitButton label={copy.submitLabel} pending={pending} />
        </div>
      </form>
    </div>
  );
}
